/*
Copyright (c) (2022 - infinity) Maifee Ul Asad
*/

/*
MIT License

Copyright (c) 2017 Pavel Dobryakov

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

import { type PathFollower } from "./PathFollower"
import { type FluidPath } from "./paths"
import {
  baseVertexShaderInstructions,
  blurVertexShaderInstructions,
  blurShaderInstructions,
  copyShaderInstructions,
  clearShaderInstructions,
  colorShaderInstructions,
  checkerboardShaderTransparentInstructions,
  checkerboardShaderOpaqueInstructions,
  displayShaderSourceInstructions,
  bloomPrefilterShaderInstructions,
  bloomBlurShaderInstructions,
  bloomFinalShaderInstructions,
  sunraysMaskShaderInstructions,
  sunraysShaderInstructions,
  splatShaderInstructions,
  advectionShaderInstructions,
  divergenceShaderInstructions,
  curlShaderInstructions,
  vorticityShaderInstructions,
  pressureShaderInstructions,
  gradientSubtractShaderInstructions,
} from "./shaders"

interface Config {
  TRIGGER: string;
  SIM_RESOLUTION: number;
  DYE_RESOLUTION: number;
  CAPTURE_RESOLUTION: number;
  DENSITY_DISSIPATION: number;
  VELOCITY_DISSIPATION: number;
  PRESSURE: number;
  PRESSURE_ITERATIONS: number;
  CURL: number;
  SPLAT_RADIUS: number;
  SPLAT_FORCE: number;
  SPLAT_COUNT: number;
  SHADING: boolean;
  COLORFUL: boolean;
  COLOR_UPDATE_SPEED: number;
  PAUSED: boolean;
  BACK_COLOR: { r: number; g: number; b: number };
  TRANSPARENT: boolean;
  BLOOM: boolean;
  BLOOM_ITERATIONS: number;
  BLOOM_RESOLUTION: number;
  BLOOM_INTENSITY: number;
  BLOOM_THRESHOLD: number;
  BLOOM_SOFT_KNEE: number;
  SUNRAYS: boolean;
  SUNRAYS_RESOLUTION: number;
  SUNRAYS_WEIGHT: number;
  IMMEDIATE?: boolean;
}

export type FluidConfig = Config;

type WebGLTextureFormat = { internalFormat: number; format: number; }

interface FBO {
  texture: WebGLTexture;
  fbo: WebGLFramebuffer;
  width: number;
  height: number;
  texelSizeX: number;
  texelSizeY: number;
  attach(id: number): number;
}

interface DoubleFBO {
  width: number;
  height: number;
  texelSizeX: number;
  texelSizeY: number;
  read: FBO;
  write: FBO;
  swap(): void;
}

interface Texture {
  texture: WebGLTexture;
  width: number;
  height: number;
  attach(id: number): number;
}

interface Color {
  r: number;
  g: number;
  b: number;
}

interface Uniforms {
  [key: string]: WebGLUniformLocation | null;
}

export function fluidSim(el: HTMLCanvasElement, configParam : Partial<Config>, pathFollower?: PathFollower) {
  const canvas = el
  resizeCanvas()

  const config: Config = {
    TRIGGER: 'hover',
    SIM_RESOLUTION: 1024,
    DYE_RESOLUTION: 1024,
    CAPTURE_RESOLUTION: 1024,
    DENSITY_DISSIPATION: 4,
    VELOCITY_DISSIPATION: 3.4,
    PRESSURE: 0.85,
    PRESSURE_ITERATIONS: 20,
    CURL: 2,
    SPLAT_RADIUS: 0.10,
    SPLAT_FORCE: 2000,
    SPLAT_COUNT: Math.floor(Math.random() * 20) + 5,
    SHADING: true,
    COLORFUL: true,
    COLOR_UPDATE_SPEED: 3,
    PAUSED: false,
    BACK_COLOR: { r: 0, g: 0, b: 0 },
    TRANSPARENT: false,
    BLOOM: true,
    BLOOM_ITERATIONS: 8,
    BLOOM_RESOLUTION: 256,
    BLOOM_INTENSITY: 0.8,
    BLOOM_THRESHOLD: 0.6,
    BLOOM_SOFT_KNEE: 0.7,
    SUNRAYS: true,
    SUNRAYS_RESOLUTION: 196,
    SUNRAYS_WEIGHT: 1.0,
    ...configParam,
  };

  class Pointer {
    id = -1;
    texcoordX = 0;
    texcoordY = 0;
    prevTexcoordX = 0;
    prevTexcoordY = 0;
    deltaX = 0;
    deltaY = 0;
    down = false;
    moved = false;
    color: { r: number; g: number; b: number } = { r: 30, g: 0, b: 300 };
  }

  const pointers: Pointer[] = []
  const splatStack: number[] = []
  const bloomFramebuffers: FBO[] = []
  pointers.push(new Pointer())

  const isMobile = (): boolean => {
    return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  }

  const { gl, ext } = getWebGLContext(canvas)

  if (isMobile()) {
    config.DYE_RESOLUTION = 512
    config.SIM_RESOLUTION = 512
    config.BLOOM_ITERATIONS = 4 // Reduce bloom iterations for better performance
    config.SUNRAYS_RESOLUTION = 128 // Reduce sunrays resolution
    config.PRESSURE_ITERATIONS = 15 // Reduce pressure iterations for better performance
    config.SPLAT_COUNT = Math.floor(Math.random() * 10) + 3 // Reduce initial splats
  }
  if (!ext.supportLinearFiltering) {
    config.DYE_RESOLUTION = 512
    config.SHADING = false
    config.BLOOM = false
    config.SUNRAYS = false
  }

  // startGUI()

  function getWebGLContext(canvas: HTMLCanvasElement) {
    // Check if canvas has valid dimensions
    if (canvas.width === 0 || canvas.height === 0) {
      throw new Error('Canvas must have valid dimensions before initializing WebGL')
    }

    console.log('Initializing WebGL context...', { width: canvas.width, height: canvas.height })

    // Start with basic parameters that are widely supported
    const baseParams: WebGLContextAttributes = {
      alpha: true,
      depth: false,
      stencil: false,
      antialias: false,
      preserveDrawingBuffer: false
    }

    let gl: any = null
    let isWebGL2 = false

    // Try WebGL2 first
    try {
      gl = canvas.getContext('webgl2', baseParams)
      if (gl) {
        isWebGL2 = true
        console.log('WebGL2 context created successfully')
        console.log('WebGL context details:', {
          renderer: gl.getParameter(gl.getExtension('WEBGL_debug_renderer_info')?.UNMASKED_RENDERER_WEBGL || gl.RENDERER),
          vendor: gl.getParameter(gl.getExtension('WEBGL_debug_renderer_info')?.UNMASKED_VENDOR_WEBGL || gl.VENDOR),
          version: gl.getParameter(gl.VERSION),
          maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE)
        })
      }
    } catch (e) {
      console.warn('WebGL2 not available, trying WebGL1:', e)
    }

    // Fallback to WebGL1 if WebGL2 failed
    if (!gl) {
      try {
        gl = canvas.getContext('webgl', baseParams) || canvas.getContext('experimental-webgl', baseParams)
        if (gl) {
          console.log('WebGL1 context created successfully')
          console.log('WebGL context details:', {
            renderer: gl.getParameter(gl.getExtension('WEBGL_debug_renderer_info')?.UNMASKED_RENDERER_WEBGL || gl.RENDERER),
            vendor: gl.getParameter(gl.getExtension('WEBGL_debug_renderer_info')?.UNMASKED_VENDOR_WEBGL || gl.VENDOR),
            version: gl.getParameter(gl.VERSION),
            maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE)
          })
        }
      } catch (e) {
        console.error('WebGL1 also failed:', e)
      }
    }

    if (!gl) {
      throw new Error('WebGL not supported. Please update your browser or enable hardware acceleration.')
    }

    // Optional: Try to set power preference if supported (don't fail if not)
    try {
      if (isMobile() && 'powerPreference' in baseParams) {
        // This is a newer feature, only apply if browser supports it
        const mobileParams = { ...baseParams, powerPreference: 'low-power' as const }
        const testGl = canvas.getContext('webgl', mobileParams)
        if (testGl) {
          gl = testGl
          console.log('Low-power mode enabled for mobile')
        }
      }
    } catch (e) {
      console.warn('Power preference not supported, using default:', e)
    }

    // Check for required WebGL extensions (don't fail, just warn)
    if (isMobile()) {
      const requiredExtensions = ['OES_texture_float', 'OES_texture_float_linear']
      for (const ext of requiredExtensions) {
        if (!gl.getExtension(ext)) {
          console.warn(`WebGL extension ${ext} not available. Some visual effects may be reduced.`)
        }
      }
    }

    let halfFloat: OES_texture_half_float | null
    let supportLinearFiltering: OES_texture_half_float_linear | null
    if (isWebGL2) {
      gl.getExtension('EXT_color_buffer_float')
      supportLinearFiltering = gl.getExtension('OES_texture_float_linear') as OES_texture_half_float_linear | null
    } else {
      halfFloat = gl.getExtension('OES_texture_half_float') as OES_texture_half_float | null
      supportLinearFiltering = gl.getExtension('OES_texture_half_float_linear') as OES_texture_half_float_linear | null
    }

    gl.clearColor(0.0, 0.0, 0.0, 1.0)

    const halfFloatTexType = isWebGL2 ? gl.HALF_FLOAT : halfFloat!.HALF_FLOAT_OES
    let formatRGBA: WebGLTextureFormat | null
    let formatRG: WebGLTextureFormat | null
    let formatR: WebGLTextureFormat | null

    if (isWebGL2) {
      formatRGBA = getSupportedFormat(gl, gl.RGBA16F, gl.RGBA, halfFloatTexType)
      formatRG = getSupportedFormat(gl, gl.RG16F, gl.RG, halfFloatTexType)
      formatR = getSupportedFormat(gl, gl.R16F, gl.RED, halfFloatTexType)
    } else {
      formatRGBA = getSupportedFormat(gl, gl.RGBA, gl.RGBA, halfFloatTexType)
      formatRG = getSupportedFormat(gl, gl.RGBA, gl.RGBA, halfFloatTexType)
      formatR = getSupportedFormat(gl, gl.RGBA, gl.RGBA, halfFloatTexType)
    }

    return {
      gl,
      ext: {
        formatRGBA,
        formatRG,
        formatR,
        halfFloatTexType,
        supportLinearFiltering
      }
    }
  }

  function getSupportedFormat(gl: any, internalFormat: number, format: number, type: number): WebGLTextureFormat | null {
    if (!supportRenderTextureFormat(gl, internalFormat, format, type)) {
      switch (internalFormat) {
        case gl.R16F:
          return getSupportedFormat(gl, gl.RG16F, gl.RG, type)
        case gl.RG16F:
          return getSupportedFormat(gl, gl.RGBA16F, gl.RGBA, type)
        default:
          return null
      }
    }

    return {
      internalFormat,
      format
    }
  }

  function supportRenderTextureFormat(gl: any, internalFormat: number, format: number, type: number): boolean {
    const texture = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, 4, 4, 0, format, type, null)

    const fbo = gl.createFramebuffer()
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo)
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0)

    const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER)
    return status === gl.FRAMEBUFFER_COMPLETE
  }

  class Material {
    vertexShader: WebGLShader;
    fragmentShaderSource: string;
    programs: { [key: number]: WebGLProgram };
    activeProgram: WebGLProgram | null;
    uniforms: Uniforms;

    constructor(vertexShader: WebGLShader, fragmentShaderSource: string) {
      this.vertexShader = vertexShader
      this.fragmentShaderSource = fragmentShaderSource
      this.programs = {}
      this.activeProgram = null
      this.uniforms = {}
    }

    setKeywords(keywords: string[]) {
      let hash = 0
      for (let i = 0; i < keywords.length; i++)
        {hash += hashCode(keywords[i])}

      let program = this.programs[hash]
      if (program == null) {
        const fragmentShader = compileShader(gl.FRAGMENT_SHADER, this.fragmentShaderSource, keywords)
        program = createProgram(this.vertexShader, fragmentShader)
        this.programs[hash] = program
      }

      if (program === this.activeProgram) {return}

      this.uniforms = getUniforms(program)
      this.activeProgram = program
    }

    bind() {
      gl.useProgram(this.activeProgram)
    }
  }

  class Program {
    uniforms: Uniforms;
    program: WebGLProgram;

    constructor(vertexShader: WebGLShader, fragmentShader: WebGLShader) {
      this.uniforms = {}
      this.program = createProgram(vertexShader, fragmentShader)
      this.uniforms = getUniforms(this.program)
    }

    bind() {
      gl.useProgram(this.program)
    }
  }

  function createProgram(vertexShader: WebGLShader, fragmentShader: WebGLShader): WebGLProgram {
    const program = gl.createProgram()
    if (!program) {
      throw new Error('Failed to create program')
    }
    gl.attachShader(program, vertexShader)
    gl.attachShader(program, fragmentShader)
    gl.linkProgram(program)

    if (!gl.getProgramParameter(program, gl.LINK_STATUS))
      {throw gl.getProgramInfoLog(program)}

    return program
  }

  function getUniforms(program: WebGLProgram): Uniforms {
    const uniforms: Uniforms = {}
    const uniformCount = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS)
    for (let i = 0; i < uniformCount; i++) {
      const uniformName = gl.getActiveUniform(program, i)?.name
      if (uniformName) {
        uniforms[uniformName] = gl.getUniformLocation(program, uniformName)
      }
    }
    return uniforms
  }

  const compileShader = (type: GLenum, source: string, keywords?: string[]): WebGLShader => {
    source = addKeywords(source, keywords)

    const shader: WebGLShader | null = gl.createShader(type)
    if (!shader) {
      throw new Error('Failed to create shader')
    }
    gl.shaderSource(shader, source)
    gl.compileShader(shader)

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      throw new Error(gl.getShaderInfoLog(shader))
    }

    return shader
  }

  const addKeywords = (source: string, keywords?: string[]): string => {
    if (keywords == null) {return source;}
    const keywordsString = keywords.map(keyword => `#define ${keyword}`).join('\n');
    return `${keywordsString}\n${source}`;
  };

  const baseVertexShader = compileShader(gl.VERTEX_SHADER, baseVertexShaderInstructions)

  const blurVertexShader = compileShader(gl.VERTEX_SHADER, blurVertexShaderInstructions)

  const blurShader = compileShader(gl.FRAGMENT_SHADER, blurShaderInstructions)

  const copyShader = compileShader(gl.FRAGMENT_SHADER, copyShaderInstructions)

  const clearShader = compileShader(gl.FRAGMENT_SHADER, clearShaderInstructions)

  const colorShader = compileShader(gl.FRAGMENT_SHADER, colorShaderInstructions)

  const checkerboardShader = compileShader(gl.FRAGMENT_SHADER, config.TRANSPARENT ? checkerboardShaderTransparentInstructions : checkerboardShaderOpaqueInstructions)

  const displayShaderSource = displayShaderSourceInstructions

  const bloomPrefilterShader = compileShader(gl.FRAGMENT_SHADER, bloomPrefilterShaderInstructions)

  const bloomBlurShader = compileShader(gl.FRAGMENT_SHADER, bloomBlurShaderInstructions)

  const bloomFinalShader = compileShader(gl.FRAGMENT_SHADER, bloomFinalShaderInstructions)

  const sunraysMaskShader = compileShader(gl.FRAGMENT_SHADER, sunraysMaskShaderInstructions)

  const sunraysShader = compileShader(gl.FRAGMENT_SHADER, sunraysShaderInstructions)

  const splatShader = compileShader(gl.FRAGMENT_SHADER, splatShaderInstructions)

  const advectionShader = compileShader(gl.FRAGMENT_SHADER, advectionShaderInstructions,
    ext.supportLinearFiltering ? undefined : ['MANUAL_FILTERING']
  )

  const divergenceShader = compileShader(gl.FRAGMENT_SHADER, divergenceShaderInstructions)

  const curlShader = compileShader(gl.FRAGMENT_SHADER, curlShaderInstructions)

  const vorticityShader = compileShader(gl.FRAGMENT_SHADER, vorticityShaderInstructions)

  const pressureShader = compileShader(gl.FRAGMENT_SHADER, pressureShaderInstructions)

  const gradientSubtractShader = compileShader(gl.FRAGMENT_SHADER, gradientSubtractShaderInstructions)

  const blit = (() => {
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer())
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, -1, 1, 1, 1, 1, -1]), gl.STATIC_DRAW)
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, gl.createBuffer())
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array([0, 1, 2, 0, 2, 3]), gl.STATIC_DRAW)
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)
    gl.enableVertexAttribArray(0)

    return (destination: WebGLFramebuffer | null) => {
      gl.bindFramebuffer(gl.FRAMEBUFFER, destination)
      gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0)
    }
  })()

  let dye: DoubleFBO
  let velocity: DoubleFBO
  let divergence: FBO
  let curl: FBO
  let pressure: DoubleFBO
  let bloom: FBO
  let sunrays: FBO
  let sunraysTemp: FBO

  const ditheringTexture = createTextureAsync()

  const blurProgram = new Program(blurVertexShader, blurShader)
  const copyProgram = new Program(baseVertexShader, copyShader)
  const clearProgram = new Program(baseVertexShader, clearShader)
  const colorProgram = new Program(baseVertexShader, colorShader)
  const checkerboardProgram = new Program(baseVertexShader, checkerboardShader)
  const bloomPrefilterProgram = new Program(baseVertexShader, bloomPrefilterShader)
  const bloomBlurProgram = new Program(baseVertexShader, bloomBlurShader)
  const bloomFinalProgram = new Program(baseVertexShader, bloomFinalShader)
  const sunraysMaskProgram = new Program(baseVertexShader, sunraysMaskShader)
  const sunraysProgram = new Program(baseVertexShader, sunraysShader)
  const splatProgram = new Program(baseVertexShader, splatShader)
  const advectionProgram = new Program(baseVertexShader, advectionShader)
  const divergenceProgram = new Program(baseVertexShader, divergenceShader)
  const curlProgram = new Program(baseVertexShader, curlShader)
  const vorticityProgram = new Program(baseVertexShader, vorticityShader)
  const pressureProgram = new Program(baseVertexShader, pressureShader)
  const gradienSubtractProgram = new Program(baseVertexShader, gradientSubtractShader)

  const displayMaterial = new Material(baseVertexShader, displayShaderSource)

  function initFramebuffers() {
    const simRes = getResolution(config.SIM_RESOLUTION)
    const dyeRes = getResolution(config.DYE_RESOLUTION)

    const texType = ext.halfFloatTexType
    const rgba = ext.formatRGBA
    const rg = ext.formatRG
    const r = ext.formatR
    const filtering = ext.supportLinearFiltering ? gl.LINEAR : gl.NEAREST

    if (dye == null)
      {dye = createDoubleFBO(dyeRes.width, dyeRes.height, rgba!.internalFormat, rgba!.format, texType, filtering)}
    else
      {dye = resizeDoubleFBO(dye, dyeRes.width, dyeRes.height, rgba!.internalFormat, rgba!.format, texType, filtering)}

    if (velocity == null)
      {velocity = createDoubleFBO(simRes.width, simRes.height, rg!.internalFormat, rg!.format, texType, filtering)}
    else
      {velocity = resizeDoubleFBO(velocity, simRes.width, simRes.height, rg!.internalFormat, rg!.format, texType, filtering)}

    divergence = createFBO(simRes.width, simRes.height, r!.internalFormat, r!.format, texType, gl.NEAREST)
    curl = createFBO(simRes.width, simRes.height, r!.internalFormat, r!.format, texType, gl.NEAREST)
    pressure = createDoubleFBO(simRes.width, simRes.height, r!.internalFormat, r!.format, texType, gl.NEAREST)

    initBloomFramebuffers()
    initSunraysFramebuffers()
  }

  function initBloomFramebuffers() {
    const res = getResolution(config.BLOOM_RESOLUTION)

    const texType = ext.halfFloatTexType
    const rgba = ext.formatRGBA
    const filtering = ext.supportLinearFiltering ? gl.LINEAR : gl.NEAREST

    bloom = createFBO(res.width, res.height, rgba!.internalFormat, rgba!.format, texType, filtering)

    bloomFramebuffers.length = 0
    for (let i = 0; i < config.BLOOM_ITERATIONS; i++) {
      const width = res.width >> (i + 1)
      const height = res.height >> (i + 1)

      if (width < 2 || height < 2) {break}

      const fbo = createFBO(width, height, rgba!.internalFormat, rgba!.format, texType, filtering)
      bloomFramebuffers.push(fbo)
    }
  }

  function initSunraysFramebuffers() {
    const res = getResolution(config.SUNRAYS_RESOLUTION)

    const texType = ext.halfFloatTexType
    const r = ext.formatR
    const filtering = ext.supportLinearFiltering ? gl.LINEAR : gl.NEAREST

    sunrays = createFBO(res.width, res.height, r!.internalFormat, r!.format, texType, filtering)
    sunraysTemp = createFBO(res.width, res.height, r!.internalFormat, r!.format, texType, filtering)
  }

  function createFBO(w: number, h: number, internalFormat: number, format: number, type: number, param: number): FBO {
    gl.activeTexture(gl.TEXTURE0)
    const texture = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, param)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, param)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, w, h, 0, format, type, null)

    const fbo = gl.createFramebuffer()
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo)
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0)
    gl.viewport(0, 0, w, h)
    gl.clear(gl.COLOR_BUFFER_BIT)

    const texelSizeX = 1.0 / w
    const texelSizeY = 1.0 / h

    return {
      texture,
      fbo,
      width: w,
      height: h,
      texelSizeX,
      texelSizeY,
      attach(id: number) {
        gl.activeTexture(gl.TEXTURE0 + id)
        gl.bindTexture(gl.TEXTURE_2D, texture)
        return id
      }
    }
  }

  function createDoubleFBO(w: number, h: number, internalFormat: number, format: number, type: number, param: number): DoubleFBO {
    let fbo1 = createFBO(w, h, internalFormat, format, type, param)
    let fbo2 = createFBO(w, h, internalFormat, format, type, param)

    return {
      width: w,
      height: h,
      texelSizeX: fbo1.texelSizeX,
      texelSizeY: fbo1.texelSizeY,
      get read() {
        return fbo1
      },
      set read(value) {
        fbo1 = value
      },
      get write() {
        return fbo2
      },
      set write(value) {
        fbo2 = value
      },
      swap() {
        const temp = fbo1
        fbo1 = fbo2
        fbo2 = temp
      }
    }
  }

  function resizeFBO(target: FBO, w: number, h: number, internalFormat: number, format: number, type: number, param: number): FBO {
    const newFBO = createFBO(w, h, internalFormat, format, type, param)
    copyProgram.bind()
    gl.uniform1i(copyProgram.uniforms.uTexture, target.attach(0))
    blit(newFBO.fbo)
    return newFBO
  }

  function resizeDoubleFBO(target: DoubleFBO, w: number, h: number, internalFormat: number, format: number, type: number, param: number): DoubleFBO {
    if (target.width === w && target.height === h)
      {return target}
    target.read = resizeFBO(target.read, w, h, internalFormat, format, type, param)
    target.write = createFBO(w, h, internalFormat, format, type, param)
    target.width = w
    target.height = h
    target.texelSizeX = 1.0 / w
    target.texelSizeY = 1.0 / h
    return target
  }

  function createTextureAsync(url?: string): Texture {
    const texture = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, 1, 1, 0, gl.RGB, gl.UNSIGNED_BYTE, new Uint8Array([255, 255, 255]))

    const obj = {
      texture,
      width: 1,
      height: 1,
      attach(id: number) {
        gl.activeTexture(gl.TEXTURE0 + id)
        gl.bindTexture(gl.TEXTURE_2D, texture)
        return id
      }
    }

    const image = new Image()
    image.onload = () => {
      obj.width = image.width
      obj.height = image.height
      gl.bindTexture(gl.TEXTURE_2D, texture)
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, image)
    }
    if (url) { image.src = url; }

    return obj
  }

  function updateKeywords() {
    const displayKeywords = []
    if (config.SHADING) {displayKeywords.push('SHADING')}
    if (config.BLOOM) {displayKeywords.push('BLOOM')}
    if (config.SUNRAYS) {displayKeywords.push('SUNRAYS')}
    displayMaterial.setKeywords(displayKeywords)
  }

  updateKeywords()
  initFramebuffers()
  if (config.IMMEDIATE) { multipleSplats(config.SPLAT_COUNT); }

  let lastUpdateTime = Date.now()
  let colorUpdateTimer = 0.0
  let lastFrameTime = 0
  const targetFPS = isMobile() ? 45 : 60 // Lower FPS target for mobile
  const frameInterval = 1000 / targetFPS
  update()

  function update() {
    const now = performance.now()
    const dt = calcDeltaTime()

    if (resizeCanvas())
      {initFramebuffers()}
    updateColors(dt)
    applyInputs()
    if (!config.PAUSED)
      {step(dt)}
    render(null)

    // Frame rate limiting for better mobile performance
    if (isMobile() && now - lastFrameTime < frameInterval) {
      setTimeout(() => requestAnimationFrame(update), frameInterval - (now - lastFrameTime))
    } else {
      lastFrameTime = now
      requestAnimationFrame(update)
    }
  }

  function calcDeltaTime() {
    const now = Date.now()
    let dt = (now - lastUpdateTime) / 1000
    dt = Math.min(dt, 0.016666)
    lastUpdateTime = now
    return dt
  }

  function resizeCanvas() {
    const width = scaleByPixelRatio(canvas.clientWidth)
    const height = scaleByPixelRatio(canvas.clientHeight)
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width
      canvas.height = height
      return true
    }
    return false
  }

  function updateColors(dt: number) {
    if (!config.COLORFUL) {return}

    colorUpdateTimer += dt * config.COLOR_UPDATE_SPEED
    if (colorUpdateTimer >= 1) {
      colorUpdateTimer = wrap(colorUpdateTimer, 0, 1)
      pointers.forEach(p => {
        p.color = generateColor()
      })
    }
  }

  function applyInputs() {
    if (splatStack.length > 0)
      {multipleSplats(splatStack.pop() as number)}

    // Handle path following if active
    if (pathFollower && pathFollower.isFollowingPath()) {
      const pathPosition = pathFollower.getCurrentPosition(canvas.width, canvas.height)
      if (pathPosition) {
        // Create a virtual pointer for path following
        const pathPointer: Pointer = new Pointer()
        pathPointer.texcoordX = pathPosition.x / canvas.width
        pathPointer.texcoordY = 1.0 - pathPosition.y / canvas.height
        pathPointer.deltaX = correctDeltaX(pathPosition.deltaX)
        pathPointer.deltaY = correctDeltaY(pathPosition.deltaY)
        pathPointer.down = true
        pathPointer.moved = true
        pathPointer.color = pathPosition.color || generateColor()
        
        splatPointer(pathPointer)
      }
    } else {
      // Normal pointer input handling when not following a path
      pointers.forEach(p => {
        if (p.moved) {
          p.moved = false
          splatPointer(p)
        }
      })
    }
  }

  function step(dt: number) {
    gl.disable(gl.BLEND)
    gl.viewport(0, 0, velocity.width, velocity.height)

    curlProgram.bind()
    gl.uniform2f(curlProgram.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY)
    gl.uniform1i(curlProgram.uniforms.uVelocity, velocity.read.attach(0))
    blit(curl.fbo)

    vorticityProgram.bind()
    gl.uniform2f(vorticityProgram.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY)
    gl.uniform1i(vorticityProgram.uniforms.uVelocity, velocity.read.attach(0))
    gl.uniform1i(vorticityProgram.uniforms.uCurl, curl.attach(1))
    gl.uniform1f(vorticityProgram.uniforms.curl, config.CURL)
    gl.uniform1f(vorticityProgram.uniforms.dt, dt)
    blit(velocity.write.fbo)
    velocity.swap()

    divergenceProgram.bind()
    gl.uniform2f(divergenceProgram.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY)
    gl.uniform1i(divergenceProgram.uniforms.uVelocity, velocity.read.attach(0))
    blit(divergence.fbo)

    clearProgram.bind()
    gl.uniform1i(clearProgram.uniforms.uTexture, pressure.read.attach(0))
    gl.uniform1f(clearProgram.uniforms.value, config.PRESSURE)
    blit(pressure.write.fbo)
    pressure.swap()

    pressureProgram.bind()
    gl.uniform2f(pressureProgram.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY)
    gl.uniform1i(pressureProgram.uniforms.uDivergence, divergence.attach(0))
    for (let i = 0; i < config.PRESSURE_ITERATIONS; i++) {
      gl.uniform1i(pressureProgram.uniforms.uPressure, pressure.read.attach(1))
      blit(pressure.write.fbo)
      pressure.swap()
    }

    gradienSubtractProgram.bind()
    gl.uniform2f(gradienSubtractProgram.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY)
    gl.uniform1i(gradienSubtractProgram.uniforms.uPressure, pressure.read.attach(0))
    gl.uniform1i(gradienSubtractProgram.uniforms.uVelocity, velocity.read.attach(1))
    blit(velocity.write.fbo)
    velocity.swap()

    advectionProgram.bind()
    gl.uniform2f(advectionProgram.uniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY)
    if (!ext.supportLinearFiltering)
      {gl.uniform2f(advectionProgram.uniforms.dyeTexelSize, velocity.texelSizeX, velocity.texelSizeY)}
    const velocityId = velocity.read.attach(0)
    gl.uniform1i(advectionProgram.uniforms.uVelocity, velocityId)
    gl.uniform1i(advectionProgram.uniforms.uSource, velocityId)
    gl.uniform1f(advectionProgram.uniforms.dt, dt)
    gl.uniform1f(advectionProgram.uniforms.dissipation, config.VELOCITY_DISSIPATION)
    blit(velocity.write.fbo)
    velocity.swap()

    gl.viewport(0, 0, dye.width, dye.height)

    if (!ext.supportLinearFiltering)
      {gl.uniform2f(advectionProgram.uniforms.dyeTexelSize, dye.texelSizeX, dye.texelSizeY)}
    gl.uniform1i(advectionProgram.uniforms.uVelocity, velocity.read.attach(0))
    gl.uniform1i(advectionProgram.uniforms.uSource, dye.read.attach(1))
    gl.uniform1f(advectionProgram.uniforms.dissipation, config.DENSITY_DISSIPATION)
    blit(dye.write.fbo)
    dye.swap()
  }

  function render(target: FBO | null) {
    if (config.BLOOM)
      {applyBloom(dye.read, bloom)}
    if (config.SUNRAYS) {
      applySunrays(dye.read, dye.write, sunrays)
      blur(sunrays, sunraysTemp, 1)
    }

    if (target == null || !config.TRANSPARENT) {
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
      gl.enable(gl.BLEND)
    } else {
      gl.disable(gl.BLEND)
    }

    const width = target == null ? gl.drawingBufferWidth : target.width
    const height = target == null ? gl.drawingBufferHeight : target.height
    gl.viewport(0, 0, width, height)

    const fbo = target == null ? null : target.fbo
    if (!config.TRANSPARENT)
      {drawColor(fbo, normalizeColor(config.BACK_COLOR))}
    if (target == null && config.TRANSPARENT)
      {drawCheckerboard(fbo)}
    drawDisplay(fbo, width, height)
  }

  function drawColor(fbo: WebGLFramebuffer | null, color: Color) {
    colorProgram.bind()
    gl.uniform4f(colorProgram.uniforms.color, color.r, color.g, color.b, 1)
    blit(fbo)
  }

  function drawCheckerboard(fbo: WebGLFramebuffer | null) {
    checkerboardProgram.bind()
    gl.uniform1f(checkerboardProgram.uniforms.aspectRatio, canvas.width / canvas.height)
    blit(fbo)
  }

  function drawDisplay(fbo: WebGLFramebuffer | null, width: number, height: number) {
    displayMaterial.bind()
    if (config.SHADING)
      {gl.uniform2f(displayMaterial.uniforms.texelSize, 1.0 / width, 1.0 / height)}
    gl.uniform1i(displayMaterial.uniforms.uTexture, dye.read.attach(0))
    if (config.BLOOM) {
      gl.uniform1i(displayMaterial.uniforms.uBloom, bloom.attach(1))
      gl.uniform1i(displayMaterial.uniforms.uDithering, ditheringTexture.attach(2))
      const scale = getTextureScale(ditheringTexture, width, height)
      gl.uniform2f(displayMaterial.uniforms.ditherScale, scale.x, scale.y)
    }
    if (config.SUNRAYS)
      {gl.uniform1i(displayMaterial.uniforms.uSunrays, sunrays.attach(3))}
    blit(fbo)
  }

  function applyBloom(source: FBO, destination: FBO) {
    if (bloomFramebuffers.length < 2)
      {return}

    let last = destination

    gl.disable(gl.BLEND)
    bloomPrefilterProgram.bind()
    const knee = config.BLOOM_THRESHOLD * config.BLOOM_SOFT_KNEE + 0.0001
    const curve0 = config.BLOOM_THRESHOLD - knee
    const curve1 = knee * 2
    const curve2 = 0.25 / knee
    gl.uniform3f(bloomPrefilterProgram.uniforms.curve, curve0, curve1, curve2)
    gl.uniform1f(bloomPrefilterProgram.uniforms.threshold, config.BLOOM_THRESHOLD)
    gl.uniform1i(bloomPrefilterProgram.uniforms.uTexture, source.attach(0))
    gl.viewport(0, 0, last.width, last.height)
    blit(last.fbo)

    bloomBlurProgram.bind()
    for (let i = 0; i < bloomFramebuffers.length; i++) {
      const dest = bloomFramebuffers[i]
      gl.uniform2f(bloomBlurProgram.uniforms.texelSize, last.texelSizeX, last.texelSizeY)
      gl.uniform1i(bloomBlurProgram.uniforms.uTexture, last.attach(0))
      gl.viewport(0, 0, dest.width, dest.height)
      blit(dest.fbo)
      last = dest
    }

    gl.blendFunc(gl.ONE, gl.ONE)
    gl.enable(gl.BLEND)

    for (let i = bloomFramebuffers.length - 2; i >= 0; i--) {
      const baseTex = bloomFramebuffers[i]
      gl.uniform2f(bloomBlurProgram.uniforms.texelSize, last.texelSizeX, last.texelSizeY)
      gl.uniform1i(bloomBlurProgram.uniforms.uTexture, last.attach(0))
      gl.viewport(0, 0, baseTex.width, baseTex.height)
      blit(baseTex.fbo)
      last = baseTex
    }

    gl.disable(gl.BLEND)
    bloomFinalProgram.bind()
    gl.uniform2f(bloomFinalProgram.uniforms.texelSize, last.texelSizeX, last.texelSizeY)
    gl.uniform1i(bloomFinalProgram.uniforms.uTexture, last.attach(0))
    gl.uniform1f(bloomFinalProgram.uniforms.intensity, config.BLOOM_INTENSITY)
    gl.viewport(0, 0, destination.width, destination.height)
    blit(destination.fbo)
  }

  function applySunrays(source: FBO, mask: FBO, destination: FBO) {
    gl.disable(gl.BLEND)
    sunraysMaskProgram.bind()
    gl.uniform1i(sunraysMaskProgram.uniforms.uTexture, source.attach(0))
    gl.viewport(0, 0, mask.width, mask.height)
    blit(mask.fbo)

    sunraysProgram.bind()
    gl.uniform1f(sunraysProgram.uniforms.weight, config.SUNRAYS_WEIGHT)
    gl.uniform1i(sunraysProgram.uniforms.uTexture, mask.attach(0))
    gl.viewport(0, 0, destination.width, destination.height)
    blit(destination.fbo)
  }

  function blur(target: FBO, temp: FBO, iterations: number) {
    blurProgram.bind()
    for (let i = 0; i < iterations; i++) {
      gl.uniform2f(blurProgram.uniforms.texelSize, target.texelSizeX, 0.0)
      gl.uniform1i(blurProgram.uniforms.uTexture, target.attach(0))
      blit(temp.fbo)

      gl.uniform2f(blurProgram.uniforms.texelSize, 0.0, target.texelSizeY)
      gl.uniform1i(blurProgram.uniforms.uTexture, temp.attach(0))
      blit(target.fbo)
    }
  }

  function splatPointer(pointer: Pointer) {
    const dx = pointer.deltaX * config.SPLAT_FORCE
    const dy = pointer.deltaY * config.SPLAT_FORCE
    splat(pointer.texcoordX, pointer.texcoordY, dx, dy, pointer.color)
  }

  function multipleSplats(amount: number) {
    for (let i = 0; i < amount; i++) {
      const color = generateColor()
      color.r *= 10.0
      color.g *= 10.0
      color.b *= 10.0
      const x = Math.random()
      const y = Math.random()
      const dx = 1000 * (Math.random() - 0.5)
      const dy = 1000 * (Math.random() - 0.5)
      splat(x, y, dx, dy, color)
    }
  }

  function splat(x: number, y: number, dx: number, dy: number, color: Color) {
    gl.viewport(0, 0, velocity.width, velocity.height)
    splatProgram.bind()
    gl.uniform1i(splatProgram.uniforms.uTarget, velocity.read.attach(0))
    gl.uniform1f(splatProgram.uniforms.aspectRatio, canvas.width / canvas.height)
    gl.uniform2f(splatProgram.uniforms.point, x, y)
    gl.uniform3f(splatProgram.uniforms.color, dx, dy, 0.0)
    gl.uniform1f(splatProgram.uniforms.radius, correctRadius(config.SPLAT_RADIUS / 100.0))
    blit(velocity.write.fbo)
    velocity.swap()

    gl.viewport(0, 0, dye.width, dye.height)
    gl.uniform1i(splatProgram.uniforms.uTarget, dye.read.attach(0))
    gl.uniform3f(splatProgram.uniforms.color, color.r, color.g, color.b)
    blit(dye.write.fbo)
    dye.swap()
  }

  function correctRadius(radius: number) {
    const aspectRatio = canvas.width / canvas.height
    if (aspectRatio > 1)
      {radius *= aspectRatio}
    return radius
  }

  canvas.addEventListener('mousedown', e => {
    const posX = scaleByPixelRatio(e.offsetX)
    const posY = scaleByPixelRatio(e.offsetY)
    let pointer = pointers.find(p => p.id === -1)
    if (pointer == null)
      {pointer = new Pointer()}
    updatePointerDownData(pointer, -1, posX, posY)
  })

  setTimeout(() => {
    canvas.addEventListener('mousemove', e => {
      const posX = scaleByPixelRatio(e.offsetX)
      const posY = scaleByPixelRatio(e.offsetY)
      updatePointerMoveData(pointers[0], posX, posY)
    })
  }, 500)

  window.addEventListener('mouseup', () => {
    updatePointerUpData(pointers[0])
  })

  canvas.addEventListener('touchstart', e => {
    e.preventDefault()
    const touches = e.targetTouches
    const rect = canvas.getBoundingClientRect()

    // Clear old pointers that are no longer touching
    pointers.forEach(p => {
      const stillTouching = Array.from(touches).some(t => t.identifier === p.id)
      if (!stillTouching && p.id !== -1) {
        p.down = false
      }
    })

    // Add new touch points
    for (let i = 0; i < touches.length; i++) {
      const touch = touches[i]
      let pointer = pointers.find(p => p.id === touch.identifier)

      if (!pointer) {
        pointer = new Pointer()
        pointers.push(pointer)
      }

      // Use clientX/clientY for better mobile support
      const posX = scaleByPixelRatio(touch.clientX - rect.left)
      const posY = scaleByPixelRatio(touch.clientY - rect.top)

      updatePointerDownData(pointer, touch.identifier, posX, posY)
    }
  })

  canvas.addEventListener('touchmove', e => {
    e.preventDefault()
    const touches = e.targetTouches
    const rect = canvas.getBoundingClientRect()

    for (let i = 0; i < touches.length; i++) {
      const touch = touches[i]
      const pointer = pointers.find(p => p.id === touch.identifier)

      if (pointer) {
        // Use clientX/clientY for better mobile support
        const posX = scaleByPixelRatio(touch.clientX - rect.left)
        const posY = scaleByPixelRatio(touch.clientY - rect.top)

        updatePointerMoveData(pointer, posX, posY)
      }
    }
  }, { passive: false })

  canvas.addEventListener('touchend', e => {
    e.preventDefault()
    const touches = e.changedTouches

    for (let i = 0; i < touches.length; i++) {
      const touch = touches[i]
      const pointer = pointers.find(p => p.id === touch.identifier)

      if (pointer) {
        updatePointerUpData(pointer)
        // Reset pointer for reuse
        pointer.id = -1
        pointer.down = false
        pointer.moved = false
      }
    }
  }, { passive: false })

  window.addEventListener('keydown', e => {
    if (e.code === 'KeyP')
      {config.PAUSED = !config.PAUSED}
    if (e.key === ' ')
      {splatStack.push(Math.floor(Math.random() * 20) + 5)}
  })

  function updatePointerDownData(pointer: Pointer, id: number, posX: number, posY: number) {
    pointer.id = id
    pointer.down = true
    pointer.moved = false
    pointer.texcoordX = posX / canvas.width
    pointer.texcoordY = 1.0 - posY / canvas.height
    pointer.prevTexcoordX = pointer.texcoordX
    pointer.prevTexcoordY = pointer.texcoordY
    pointer.deltaX = 0
    pointer.deltaY = 0
    pointer.color = generateColor()
  }

  function updatePointerMoveData(pointer: Pointer, posX: number, posY: number) {
    if (config.TRIGGER === 'click') {
      pointer.moved = pointer.down
    }
    pointer.prevTexcoordX = pointer.texcoordX
    pointer.prevTexcoordY = pointer.texcoordY
    pointer.texcoordX = posX / canvas.width
    pointer.texcoordY = 1.0 - posY / canvas.height
    pointer.deltaX = correctDeltaX(pointer.texcoordX - pointer.prevTexcoordX)
    pointer.deltaY = correctDeltaY(pointer.texcoordY - pointer.prevTexcoordY)
    if (config.TRIGGER === 'hover') {
      pointer.moved = Math.abs(pointer.deltaX) > 0 || Math.abs(pointer.deltaY) > 0
    }
  }

  function updatePointerUpData(pointer: Pointer) {
    pointer.down = false
  }

  function correctDeltaX(delta: number) {
    const aspectRatio = canvas.width / canvas.height
    if (aspectRatio < 1) {delta *= aspectRatio}
    return delta
  }

  function correctDeltaY(delta: number) {
    const aspectRatio = canvas.width / canvas.height
    if (aspectRatio > 1) {delta /= aspectRatio}
    return delta
  }

  function generateColor(): Color {
    const c = HSVtoRGB(Math.random(), 1.0, 1.0)
    c.r *= 0.15
    c.g *= 0.15
    c.b *= 0.15
    return c
  }

  function HSVtoRGB(h: number, s: number, v: number): Color {
    let r = 0, g = 0, b = 0
    const i = Math.floor(h * 6)
    const f = h * 6 - i
    const p = v * (1 - s)
    const q = v * (1 - f * s)
    const t = v * (1 - (1 - f) * s)

    if (i % 6 === 0) {
      r = v; g = t; b = p;
    } else if (i % 6 === 1) {
      r = q; g = v; b = p;
    } else if (i % 6 === 2) {
      r = p; g = v; b = t;
    } else if (i % 6 === 3) {
      r = p; g = q; b = v;
    } else if (i % 6 === 4) {
      r = t; g = p; b = v;
    } else if (i % 6 === 5) {
      r = v; g = p; b = q;
    }

    return {
      r,
      g,
      b
    }
  }

  function normalizeColor(input: Color): Color {
    const output = {
      r: input.r / 255,
      g: input.g / 255,
      b: input.b / 255
    }
    return output
  }

  function wrap(value: number, min: number, max: number) {
    const range = max - min
    if (range === 0) {return min}
    return (value - min) % range + min
  }

  function getResolution(resolution: number): { width: number; height: number } {
    let aspectRatio = gl.drawingBufferWidth / gl.drawingBufferHeight
    if (aspectRatio < 1)
      {aspectRatio = 1.0 / aspectRatio}

    const min = Math.round(resolution)
    const max = Math.round(resolution * aspectRatio)

    if (gl.drawingBufferWidth > gl.drawingBufferHeight)
      {return { width: max, height: min }}
    else
      {return { width: min, height: max }}
  }

  function getTextureScale(texture: Texture, width: number, height: number): { x: number; y: number } {
    return {
      x: width / texture.width,
      y: height / texture.height
    }
  }

  function scaleByPixelRatio(input: number) {
    const pixelRatio = window.devicePixelRatio || 1
    return Math.floor(input * pixelRatio)
  }

  function hashCode(s: string) {
    if (s.length === 0) {return 0}
    let hash = 0
    for (let i = 0; i < s.length; i++) {
      hash = (hash << 5) - hash + s.charCodeAt(i)
      hash |= 0 // Convert to 32bit integer
    }
    return hash
  }

  // Return an object with path control methods
  return {
    setPath: (path: FluidPath | null) => {
      if (pathFollower) {
        pathFollower.setPath(path)
      }
    },
    getPathFollower: () => pathFollower,
    isFollowingPath: () => pathFollower ? pathFollower.isFollowingPath() : false,
    stopPath: () => {
      if (pathFollower) {
        pathFollower.stop()
      }
    },
    updateConfig: (key: keyof Config, value: any) => {
      // Update the config value
      (config as any)[key] = value
      
      // Handle config changes that require reinitialization or updates
      if (['SIM_RESOLUTION', 'DYE_RESOLUTION', 'BLOOM_RESOLUTION', 'SUNRAYS_RESOLUTION'].includes(key)) {
        // Reinitialize framebuffers for resolution changes
        initFramebuffers()
      } else if (key === 'BLOOM' || key === 'SUNRAYS' || key === 'SHADING') {
        // Update display keywords for visual toggles
        updateKeywords()
      }
    },
    getConfig: () => ({ ...config })
  }
}
